import type { Address, Country } from './address';
import type { Locale } from './auth';
import type { Entity, NewEntity } from './core';
import type { ShopConfig } from '../shop-config';

export type SlotOptimizationMode =
  | 'off'
  | 'score_only'
  | 'filter_avoid'
  | 'filter_caution'
  | 'filter_neutral';

export type Branch = Entity<{
  name: string;
  readonly organisation: string;
  urlName?: string;
  address: Address;
  phone?: string;
  email?: string;
  advanceBookingHours?: number;
  maxAdvanceBookingDays?: number;
  cancellationNoticeHours?: number;
  publicBookings?: boolean;
  slotIntervalMinutes: number;
  autoStatus?: boolean;
  allowGuestCheckout?: boolean;
  slotOptimization?: SlotOptimizationMode;
  qrcode?: string;
  hasVat?: boolean;
  vatNumber?: string;
  // KAS-034: German Steuernummer — distinct from vatNumber (USt-IdNr).
  steuernummer?: string;
  config?: ShopConfig | null;
  depositEnabled?: boolean;
  depositType?: 'percent' | 'fixed';
  depositAmount?: number;
  autoRefundDepositOnFullPayment?: boolean;
}>;

export type BranchCreate = NewEntity<Branch>;

export type BranchUpdate = Partial<BranchCreate>;

type DepositConfig = Pick<
  Branch,
  'depositEnabled' | 'depositType' | 'depositAmount'
>;

/**
 * Deposit required for a booking, in display units. 0 when deposits are off,
 * no amount is configured, or the total is non-positive.
 * - 'fixed':   the configured amount, capped at the total.
 * - 'percent': amount clamped to 0..100, applied to the total.
 * Both rounded to cents.
 */
export function computeDeposit(
  branch: DepositConfig | undefined | null,
  total: number,
): number {
  if (!branch?.depositEnabled || !branch.depositAmount || total <= 0) {
    return 0;
  }
  const cents = (n: number) => Math.round(n * 100) / 100;
  if (branch.depositType === 'fixed') {
    return Math.min(cents(branch.depositAmount), total);
  }
  const percent = Math.max(0, Math.min(100, branch.depositAmount));
  return cents((total * percent) / 100);
}

export const localeToCountry: Record<Locale, Country> = {
  'en-GB': 'GB',
  'de-DE': 'DE',
} as const;

export const countryToLocale: Record<Country, Locale> = {
  GB: 'en-GB',
  DE: 'de-DE',
  US: 'en-US',
  RU: 'ru-RU',
} as const;
